import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Box, Plus, Trash2 } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent, CardHeader } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { Input } from '../components/ui/Input'
import { Badge } from '../components/ui/Badge'
import { usePermissions } from '../contexts/permissions'
import { apiError, type MaterialRow } from '../lib/industria'

const selectClass = 'h-10 w-full rounded-lg border border-gray-300 bg-white px-3 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/60'

type Tenant = { id: string; name: string; active: boolean }

type ProfileRow = {
  id: string; tenantId: string; tenantName: string | null; name: string
  mousseSkus: string[]; active: boolean; notes: string | null
  createdAt: string; author: string | null; itemCount: number
  items: {
    materialCode: string; materialName: string; unit: string
    qtyPerOrder: string; qtyPerBox: string; qtyPerUnit: string
  }[]
}

type ProfileItem = {
  id: string; materialId: string; materialCode: string; materialName: string
  unit: string; qtyPerOrder: string; qtyPerBox: string; qtyPerUnit: string; notes: string | null
}

type ProfileDetail = Omit<ProfileRow, 'itemCount' | 'author' | 'items'> & { items: ProfileItem[] }

type Line = { materialId: string; qtyPerOrder: string; qtyPerBox: string; qtyPerUnit: string }

const emptyLine: Line = { materialId: '', qtyPerOrder: '0', qtyPerBox: '0', qtyPerUnit: '0' }

// A ficha técnica diz o que entra num produto; esta diz o que um pedido gasta
// para sair. Mesmo almoxarifado, evento diferente.
export function IndustriaEmbalagemPage() {
  const queryClient = useQueryClient()
  const { can } = usePermissions()
  const canEdit = can('industria-embalagem', 'edit')

  const [tenantId, setTenantId] = useState('')
  const [name, setName] = useState('Padrão')
  const [mousse, setMousse] = useState('')
  const [lines, setLines] = useState<Line[]>([{ ...emptyLine }])
  const [editing, setEditing] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const { data: clients = [] } = useQuery<Tenant[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
  })

  const { data: profiles = [], isLoading } = useQuery<ProfileRow[]>({
    queryKey: ['embalagem', 'profiles'],
    queryFn: async () => (await api.get('/packaging-profiles')).data,
  })

  const { data: materials = [] } = useQuery<MaterialRow[]>({
    queryKey: ['industria', 'materials', 'embalagem'],
    queryFn: async () => (await api.get('/materials', { params: { kind: 'embalagem_envio' } })).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => (await api.post('/packaging-profiles', {
      id: editing,
      tenantId,
      name: name.trim() || 'Padrão',
      mousseSkus: mousse.split(',').map(s => s.trim()).filter(Boolean),
      items: lines.filter(l => l.materialId).map(l => ({
        materialId: l.materialId,
        qtyPerOrder: Number(l.qtyPerOrder || 0),
        qtyPerBox: Number(l.qtyPerBox || 0),
        qtyPerUnit: Number(l.qtyPerUnit || 0),
      })),
    })).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['embalagem'] })
      setSaved(true)
      setTimeout(() => setSaved(false), 3000)
      setEditing(null)
      setLines([{ ...emptyLine }])
      setTenantId('')
    },
    onError: (e) => setError(apiError(e, 'Não foi possível salvar a ficha')),
  })

  async function edit(id: string) {
    setError(null)
    const { data } = await api.get<ProfileDetail>(`/packaging-profiles/${id}`)
    setEditing(data.id)
    setTenantId(data.tenantId)
    setName(data.name)
    setMousse((data.mousseSkus ?? []).join(', '))
    setLines(data.items.length
      ? data.items.map(i => ({
        materialId: i.materialId,
        qtyPerOrder: String(Number(i.qtyPerOrder)),
        qtyPerBox: String(Number(i.qtyPerBox)),
        qtyPerUnit: String(Number(i.qtyPerUnit)),
      }))
      : [{ ...emptyLine }])
  }

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Ficha de embalagem</h2>
        <p className="text-sm text-gray-500">
          O que um pedido gasta para sair — baixa sozinha quando o pedido é expedido
        </p>
      </div>

      {error && <p className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}
      {saved && (
        <p className="rounded-lg bg-green-50 px-4 py-2 text-sm text-green-800">
          Ficha salva. A partir de agora, todo pedido expedido deste cliente baixa esses insumos.
        </p>
      )}

      <Card>
        <CardHeader>
          <span className="text-sm font-semibold text-gray-900">Fichas cadastradas</span>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-8 text-center text-sm text-gray-400">Carregando...</p>
          ) : profiles.length === 0 ? (
            <div className="px-5 py-10 text-center">
              <Box className="mx-auto mb-3 h-8 w-8 text-gray-300" />
              <p className="text-sm text-gray-400">
                Nenhuma ficha — sem ela a expedição não baixa embalagem, e isso é silêncio, não erro
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-2.5 text-left font-medium">Cliente</th>
                    <th className="px-4 py-2.5 text-left font-medium">Ficha</th>
                    <th className="px-4 py-2.5 text-left font-medium">Consome por pedido</th>
                    <th className="px-4 py-2.5 text-left font-medium">SKUs volumosos</th>
                    <th className="px-4 py-2.5 text-left font-medium">Situação</th>
                    <th className="px-4 py-2.5"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {profiles.map(p => (
                    <tr key={p.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 text-gray-900">{p.tenantName}</td>
                      <td className="px-4 py-2.5 text-gray-600">{p.name}</td>
                      <td className="px-4 py-2.5">
                        {(p.items ?? []).length === 0 ? (
                          <span className="text-amber-700">nenhum insumo — nada será baixado</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {p.items.map(i => (
                              <li key={i.materialCode} className="text-gray-700">
                                {i.materialName}
                                <span className="ml-1.5 font-mono text-xs text-gray-500">
                                  {[
                                    Number(i.qtyPerOrder) ? `${Number(i.qtyPerOrder)}/pedido` : null,
                                    Number(i.qtyPerBox) ? `${Number(i.qtyPerBox)}/caixa` : null,
                                    Number(i.qtyPerUnit) ? `${Number(i.qtyPerUnit)}/unidade` : null,
                                  ].filter(Boolean).join(' · ')}
                                </span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-4 py-2.5 font-mono text-xs text-gray-500">
                        {p.mousseSkus?.length ? p.mousseSkus.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-2.5">
                        {p.active ? <Badge variant="success">ativa</Badge> : <Badge>aposentada</Badge>}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        {canEdit && p.active && (
                          <Button size="sm" variant="secondary" onClick={() => edit(p.id)}>Editar</Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {canEdit && (
        <Card>
          <CardHeader>
            <span className="text-sm font-semibold text-gray-900">
              {editing ? 'Editar ficha' : 'Nova ficha'}
            </span>
            {editing && (
              <Button size="sm" variant="ghost" onClick={() => {
                setEditing(null); setTenantId(''); setLines([{ ...emptyLine }]); setName('Padrão'); setMousse('')
              }}>
                Cancelar edição
              </Button>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Cliente</label>
                <select
                  className={selectClass}
                  value={tenantId}
                  onChange={e => setTenantId(e.target.value)}
                  disabled={!!editing}
                >
                  <option value="">Selecione...</option>
                  {clients.filter(c => c.active).map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>
              <Input label="Nome da ficha" value={name} onChange={e => setName(e.target.value)} />
              <Input
                label="SKUs volumosos"
                value={mousse}
                onChange={e => setMousse(e.target.value)}
                placeholder="11, 31"
              />
            </div>
            <p className="-mt-2 text-xs text-gray-500">
              SKUs volumosos entram na regra de caixas: 2 ou mais deles no pedido forçam 2 caixas.
              Fica na ficha e não no código porque a regra nasceu de um cliente e não vale para todos.
            </p>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <span className="text-sm font-medium text-gray-700">Consumo por pedido</span>
                <Button type="button" size="sm" variant="ghost" onClick={() => setLines([...lines, { ...emptyLine }])}>
                  <Plus className="h-4 w-4" /> Insumo
                </Button>
              </div>
              <div className="mb-2 grid grid-cols-12 gap-2 px-1 text-xs uppercase tracking-wide text-gray-400">
                <span className="col-span-5">Insumo</span>
                <span className="col-span-2 text-right">Por pedido</span>
                <span className="col-span-2 text-right">Por caixa</span>
                <span className="col-span-2 text-right">Por unidade</span>
              </div>
              <div className="space-y-2">
                {lines.map((l, i) => (
                  <div key={i} className="grid grid-cols-12 items-center gap-2">
                    <select
                      className={`${selectClass} col-span-5`}
                      value={l.materialId}
                      onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, materialId: e.target.value } : x))}
                    >
                      <option value="">Selecione o insumo...</option>
                      {materials.filter(m => m.active).map(m => (
                        <option key={m.id} value={m.id}>{m.code} — {m.name}</option>
                      ))}
                    </select>
                    {(['qtyPerOrder', 'qtyPerBox', 'qtyPerUnit'] as const).map(field => (
                      <input
                        key={field}
                        className="col-span-2 h-10 rounded-lg border border-gray-300 px-2 text-right text-sm"
                        type="number"
                        step="0.0001"
                        min={0}
                        value={l[field]}
                        onChange={e => setLines(lines.map((x, j) => j === i ? { ...x, [field]: e.target.value } : x))}
                      />
                    ))}
                    <button
                      type="button"
                      className="col-span-1 text-gray-300 hover:text-red-500"
                      onClick={() => setLines(lines.filter((_, j) => j !== i))}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500">
                O consumo de um pedido é <b>por pedido + por caixa × caixas + por unidade × unidades</b>.
                Cobre os três casos reais: 1 etiqueta por pedido, 1 caixa e 0,4 m de fita por caixa,
                1 plástico bolha por unidade. Fração arredonda para cima — meia etiqueta não existe.
              </p>
            </div>

            <div className="flex justify-end">
              <Button
                loading={saveMutation.isPending}
                disabled={!tenantId || lines.filter(l => l.materialId).length === 0}
                onClick={() => { setError(null); saveMutation.mutate() }}
              >
                Salvar ficha
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
